import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRODUCTS_PER_PAGE = 9;

const ProductListing = ({ category, title, description, bgColor = '#FAFAFA' }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setPage(1);
  }, [category]);

  useEffect(() => {
    fetchProducts();
  }, [category, page]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`, {
        params: { category, page, limit: PRODUCTS_PER_PAGE }
      });
      setProducts(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const showContactMessage = (e) => {
    e.preventDefault();
    toast(
      'To place an order, reach out to us!',
      {
        description: 'Contact us via our Contact page, Instagram, or email at navsartstudio@gmail.com',
        action: { label: 'Contact Us', onClick: () => navigate('/contact') },
        duration: 6000,
      }
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A1A1A] mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: bgColor }}>
      <SEO
        title={title}
        description={`${description}. Shop ${title.toLowerCase()} by Navs Art Studio — handcrafted, one-of-a-kind resin and textured pieces.`}
        path={`/${category}`}
        image={products[0]?.image_url}
      />
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="category-title"
            >
              {title}
            </h1>
            <p className="text-[#4A4A4A] text-base max-w-2xl mx-auto leading-relaxed">
              {description}
            </p>
          </motion.div>

          {/* Products Grid */}
          {products.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-[#888888] text-lg">No products available in this category yet.</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link to={`/product/${product.id}`} className="group block" data-testid={`product-card-${product.id}`}>
                    <div className="relative overflow-hidden mb-4">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full aspect-[4/5] object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500"></div>
                      
                      {/* Badge */}
                      {product.badge && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-white/90 backdrop-blur-sm px-3 py-1 text-xs uppercase tracking-widest">
                            {product.badge.replace('-', ' ')}
                          </span>
                        </div>
                      )}
                      
                      {/* Quick Actions */}
                      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <button
                          onClick={(e) => showContactMessage(e)}
                          className="bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-colors"
                          data-testid={`wishlist-btn-${product.id}`}
                        >
                          <Heart size={18} strokeWidth={1.5} />
                        </button>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {product.name}
                      </h3>
                      <p className="text-[#888888] text-sm mb-3 line-clamp-2">
                        {product.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-medium">${product.price.toFixed(2)}</span>
                        {product.size && <span className="text-sm text-[#888888]">{product.size}</span>}
                      </div>
                      <button
                        onClick={(e) => showContactMessage(e)}
                        data-testid={`add-to-cart-btn-${product.id}`}
                        className="w-full mt-4 bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-6 py-3 text-sm uppercase tracking-widest transition-all duration-300"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex items-center justify-center gap-3 mt-20"
                data-testid="pagination-controls"
              >
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  data-testid="pagination-prev"
                  className="flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest border border-black/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p)}
                    data-testid={`pagination-page-${p}`}
                    className={`w-11 h-11 rounded-full text-sm transition-all duration-300 ${
                      p === page
                        ? 'bg-[#1A1A1A] text-white'
                        : 'border border-black/10 hover:bg-[#1A1A1A] hover:text-white'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  data-testid="pagination-next"
                  className="flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-widest border border-black/10 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#1A1A1A] hover:text-white transition-all duration-300"
                >
                  Next <ChevronRight size={16} />
                </button>
              </motion.div>
            )}

            {/* Product count */}
            {total > 0 && (
              <p className="text-center text-[#888888] text-sm mt-6" data-testid="product-count">
                Showing {products.length} of {total} piece{total !== 1 ? 's' : ''}
              </p>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListing;
